import { useState, useMemo, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import {
  createIntFormatter,
  currencyFormatter,
  textFormatter
} from "../../../../utils/formatter/formatter"

import { i18n } from "../../../../translate/i18n";

import { StableSelect } from "../../../../components/SharedComponents/sharedComponents.index";
import { SYSTEM_COLORS } from '../../../../components/system.colors'

const { t } = i18n;

const MONTH_NAMES = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

const buildKpis = ({ openToBuyTotalValue, grossExcessTotalValue, salesLossRiskTotalValue = 0 }) => [
  {
    label: textFormatter("abertura total", { textTransform: 'uppercase' }) ,
    value: createIntFormatter().format(openToBuyTotalValue),
    subtitle: textFormatter("peças em abertura", { textTransform: 'lowercase' }),
    textColor: SYSTEM_COLORS.RED.BRICK_RED
  },
  {
    label: textFormatter("excesso total", { textTransform: 'uppercase' }),
    value: createIntFormatter().format(grossExcessTotalValue),
    subtitle: textFormatter("peças em excesso", { textTransform: 'lowercase' }),
    textColor: SYSTEM_COLORS.GREEN.FOREST_GREEN
  },
  {
    label: textFormatter("perda potencial total", { textTransform: 'uppercase' }),
    value: currencyFormatter.format(salesLossRiskTotalValue),
    subtitle: textFormatter("em vendas potenciais", { textTransform: 'lowercase' }),
    textColor: SYSTEM_COLORS.YELLOW.AMBER
  },
];

const numColor = (number) => {
  const ensureTypeNumber = Number(number)
  if (ensureTypeNumber === 0) return SYSTEM_COLORS.BLACK.JET;
  return ensureTypeNumber > 0 ? SYSTEM_COLORS.GREEN.FOREST_GREEN : SYSTEM_COLORS.RED.BRICK_RED;
};

const numBg = (number, hover = false) => {
  if (number == null) return hover ? "rgba(0,0,0,0.03)" : SYSTEM_COLORS.WHITE.WHITE;
  const ensureTypeNumber = Number(number);
  if (ensureTypeNumber === 0) return hover ? "rgba(0,0,0,0.03)" : SYSTEM_COLORS.WHITE.WHITE;
  if (ensureTypeNumber > 0) return hover ? "rgba(30,138,66,0.2)" : "rgba(30,138,66,0.12)";
  return hover ? "rgba(224,82,82,0.18)" : "rgba(224,82,82,0.10)";
};

const cellBgPerda = (value, hover = false) => {
  if (!value) return hover ? "rgba(0,0,0,0.03)" : SYSTEM_COLORS.WHITE.WHITE;
  if (value <= 350000) return hover ? "rgba(217,119,6,0.14)" : "rgba(217,119,6,0.07)";
  if (value <= 500000) return hover ? "rgba(217,119,6,0.22)" : "rgba(217,119,6,0.14)";
  return hover ? "rgba(217,119,6,0.32)" : "rgba(217,119,6,0.22)";
};

const useMonthKeys = (dataByCategory) => {
  return useMemo(() => {
    if (!dataByCategory) return [];
    return [...new Set(
      Object.values(dataByCategory)
        .flat()
        .map(d => `${d.year}-${String(d.month).padStart(2, '0')}`)
    )].sort();
  }, [dataByCategory]);
};

const monthKeyToLabel = (key) => {
  const [year, month] = key.split('-');
  return `${new Date(Number(year), Number(month) - 1)
    .toLocaleString('pt-BR', { month: 'short' })
    .toUpperCase()}/${String(year).slice(2)}`;
};

const intFmt = createIntFormatter();

const mergeGroups = (dataByGroup) => {

  const isRootGroup = (groupKey, categories) => {
    const categoryNames = Object.keys(categories || {});
    return categoryNames.length === 1 && categoryNames[0] === groupKey;
  };

  return Object.entries(dataByGroup || {}).reduce((merged, [groupKey, categories]) => {
    if (isRootGroup(groupKey, categories)) return merged;

    Object.entries(categories).forEach(([categoria, rows]) => {
      merged[categoria] = [...(merged[categoria] ?? []), ...rows];
    });

    return merged;
  }, {});
};

// componentes
const CustomTooltip = ({ active, payload, label }) => {

  if (!active || !payload?.length) return null;

  const abertura = payload.find(p => p.dataKey === 'abertura')?.value ?? 0;
  const excesso = payload.find(p => p.dataKey === 'excesso')?.value ?? 0;

  return (
    <div style={{
      background: SYSTEM_COLORS.WHITE.WHITE, border: "1px solid #e0e0e0",
      borderRadius: "10px", padding: "10px 14px",
      boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
      fontSize: "13px",
    }}>
      <div style={{ fontWeight: "700", color: SYSTEM_COLORS.BLACK.EERIE_BLACK, marginBottom: 6 }}>{label}</div>
      <div style={{ color: "#666", display: "flex", justifyContent: "space-between", gap: 16 }}>
        <span>Abertura</span>
        <span style={{ fontWeight: "600", color: SYSTEM_COLORS.RED.BRICK_RED }}>
          {intFmt.format(abertura)}
        </span>
      </div>
      <div style={{ color: "#666", display: "flex", justifyContent: "space-between", gap: 16 }}>
        <span>Excesso</span>
        <span style={{ fontWeight: "600", color: SYSTEM_COLORS.GREEN.FOREST_GREEN }}>
          {intFmt.format(excesso)}
        </span>
      </div>
    </div>
  );
}

const OpenToBuyByCategoryTable = ({ ctx }) => {
  const { otbDataByCategory } = ctx || {};

  const monthKeys = useMonthKeys(otbDataByCategory);

  const otbMonths = useMemo(() => monthKeys.map(key => {
    const [year, month] = key.split('-');
    return { month: Number(month), year: Number(year), label: monthKeyToLabel(key) };
  }), [monthKeys]);

  const rows = useMemo(() => {
    if (!otbDataByCategory) return [];
    return Object.entries(otbDataByCategory).map(([categoria, monthData]) => {
      const valores = monthKeys.map(key => {
        const [year, month] = key.split('-');
        const entries = monthData.filter(
          datum => datum.year === Number(year) && datum.month === Number(month)
        );

        if (entries.length === 0) return 0;

        return entries.reduce((sum, entry) => {
          const open = Number(entry.open ?? 0);
          const excess = Number(entry.excess ?? 0);
          return sum + (open !== 0 ? open : excess);
        }, 0);
      });

      const total = valores.reduce((sum, value) => sum + value, 0);

      return { categoria, valores, total };
    });
  }, [otbDataByCategory, monthKeys]);

  const monthTotals = useMemo(
    () => monthKeys.map((_, i) => rows.reduce((sum, row) => sum + (row.valores[i] || 0), 0)),
    [rows, monthKeys]
  );

  const grandTotal = useMemo(
    () => rows.reduce((sum, row) => sum + (row.total || 0), 0),
    [rows]
  );

  return (
    <section style={{
      backgroundColor: SYSTEM_COLORS.WHITE.WHITE,
      border: `1px solid ${SYSTEM_COLORS.GRAY.LIGHT_GRAY}`,
      borderRadius: "12px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      padding: "20px 24px",
      maxBlockSize: "62vh",
      display: "flex",
      flexDirection: "column",
    }}>

      <hgroup style={{ marginBlockEnd: '0.4rem' }}>
        <h6 style={{ margin: 0 }}>
          Abertura vs Excesso por Categoria (peças)
        </h6>

        <div style={{ display: 'flex', gap: "0.4rem" }}>
          <p style={{
            display: "flex",
            alignItems: "center",
            inlineSize: "fit-content",
            fontSize: "0.6rem",
            gap: "0.2rem"
          }}>
            <span style={{
              inlineSize: 8,
              blockSize: 8,
              borderRadius: "50%",
              background: SYSTEM_COLORS.GREEN.FOREST_GREEN,
              display: "inline-block"
            }} />
            {textFormatter(t("surplus"))}
          </p>

          <p style={{
            display: "flex",
            alignItems: "center",
            inlineSize: "fit-content",
            fontSize: "0.6rem",
            gap: "0.2rem"
          }}>
            <span style={{
              inlineSize: 8,
              blockSize: 8,
              borderRadius: "50%",
              background: SYSTEM_COLORS.RED.BRICK_RED,
              display: "inline-block"
            }} />
            Abertura
          </p>
        </div>
      </hgroup>

      <div style={{ flex: 1, overflow: "auto", marginTop: "12px" }}>
        <table style={{ inlineSize: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{
                textAlign: "left",
                padding: "0.4rem",
                color: SYSTEM_COLORS.BLACK.EERIE_BLACK,
                fontWeight: "700",
                fontSize: "0.7rem",
                borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`,
                borderRight: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`,
                backgroundColor: SYSTEM_COLORS.WHITE.WHITE,
                position: "sticky",
                top: 0,
                zIndex: 2,
              }}>
                {textFormatter(t("category"), { textTransform: 'uppercase' })}
              </th>

              {otbMonths.map(({ month, year, label }) => (
                <th key={`${month}-${year}`} style={{
                  color: SYSTEM_COLORS.BLACK.EERIE_BLACK,
                  fontWeight: "700",
                  textAlign: "center",
                  fontSize: "0.7rem",
                  borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`,
                  borderRight: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`,
                  backgroundColor: "#fdf3f3",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                }}>
                  {label}
                </th>
              ))}

              <th style={{
                padding: "8px 10px",
                color: SYSTEM_COLORS.BLACK.EERIE_BLACK,
                fontWeight: "700",
                fontSize: "0.7rem",
                borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`,
                backgroundColor: "#fdf3f3",
                position: "sticky",
                top: 0,
                zIndex: 1,
              }}>
                {textFormatter(t("total"), { textTransform: 'uppercase' })}
              </th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row) => (
              <tr
                key={row.categoria}
                className="otb-row"
                style={{ borderBottom: "1px solid #f5f5f5" }}>

                <td
                  className="otb-cell"
                  style={{
                    "--bg": SYSTEM_COLORS.WHITE.WHITE,
                    "--bg-hover": "rgba(0,0,0,0.03)",
                    backgroundColor: "var(--bg)",
                    padding: "0.4rem",
                    color: "#333",
                    fontWeight: "400",
                    borderRight: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`,
                    fontSize: "0.8rem",
                  }}
                >
                  {row.categoria}
                </td>

                {row.valores.map((value, colIndex) => (
                  <td
                    key={colIndex}
                    className="otb-cell"
                    style={{
                      "--bg": numBg(value),
                      "--bg-hover": numBg(value, true),
                      backgroundColor: "var(--bg)",
                      textAlign: "center",
                      padding: "0.4rem",
                      color: value == null ? SYSTEM_COLORS.GRAY.GRAY_187 : numColor(value),
                      fontWeight: value == null ? "400" : "500",
                      borderRight: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`,
                      fontSize: "0.8rem"
                    }}
                  >
                    {value == null ? "—" : createIntFormatter().format(value)}
                  </td>
                ))}

                <td
                  className="otb-cell"
                  style={{
                    "--bg": SYSTEM_COLORS.WHITE.WHITE,
                    "--bg-hover": "rgba(0,0,0,0.05)",
                    backgroundColor: "var(--bg)",
                    textAlign: "center",
                    color: row.total == null ? SYSTEM_COLORS.GRAY.GRAY_187 : numColor(row.total),
                    fontWeight: row.total == null ? "400" : "700",
                    fontSize: "0.8rem"
                  }}
                >
                  {row.total == null ? "—" : createIntFormatter().format(row.total)}
                </td>
              </tr>
            ))}
          </tbody>

          <tfoot>
            <tr style={{
              borderTop: "2px solid #e8e8e8",
              backgroundColor: "#f5f5f5"
            }}>
              <td style={{
                padding: "0.4rem",
                fontSize: "0.7rem",
                fontWeight: "700",
                color: SYSTEM_COLORS.BLACK.EERIE_BLACK,
                borderRight: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`
              }}>
                {textFormatter(t("total"), { textTransform: 'uppercase' })}
              </td>

              {monthTotals.map((value, index) => (
                <td
                  key={index}
                  style={{
                    padding: "0.4rem",
                    textAlign: "center",
                    borderRight: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`,
                    fontSize: "0.75rem",
                    fontWeight: "700",
                    color: numColor(value),
                  }}>
                  {createIntFormatter().format(value)}
                </td>
              ))}

              <td style={{
                padding: "0.4rem",
                textAlign: "center",
                backgroundColor: "#f5f5f5",
                fontSize: "0.75rem",
                fontWeight: "700",
                color: numColor(grandTotal),
              }}>
                {createIntFormatter().format(grandTotal)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
};

const PerdaPotencialTable = ({ ctx }) => {
  const { potencialSalesLossByCategory } = ctx || {};

  const monthKeys = useMonthKeys(potencialSalesLossByCategory);

  const otbMonths = useMemo(() => monthKeys.map(key => {
    const [year, month] = key.split('-');
    return { month: Number(month), year: Number(year), label: monthKeyToLabel(key) };
  }), [monthKeys]);

  const rows = useMemo(() => {
    if (!potencialSalesLossByCategory) return [];
    return Object.entries(potencialSalesLossByCategory).map(([categoria, entries]) => {
      const valores = monthKeys.map(key => {
        const [year, month] = key.split('-');
        const entry = entries.find(
          datum => datum.year === Number(year) && datum.month === Number(month)
        );
        return entry?.salesLoss ?? null;
      });

      const total = valores.reduce((sum, value) => value != null ? sum + value : sum, 0) || null;

      return { categoria, valores, total };
    });
  }, [potencialSalesLossByCategory, monthKeys]);

  const monthTotals = useMemo(
    () => monthKeys.map((_, i) => rows.reduce((sum, row) => sum + (row.valores[i] || 0), 0)),
    [rows, monthKeys]
  );

  const grandTotal = useMemo(
    () => rows.reduce((sum, row) => sum + (row.total || 0), 0),
    [rows]
  );

  if (!potencialSalesLossByCategory || Object.keys(potencialSalesLossByCategory).length === 0) {
    return (
      <section style={{
        backgroundColor: SYSTEM_COLORS.WHITE.WHITE, border: `1px solid ${SYSTEM_COLORS.GRAY.LIGHT_GRAY}`,
        borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", padding: "20px 24px",
      }}>
        <div style={{ color: SYSTEM_COLORS.GRAY.GREY, fontSize: "13px" }}>Dados não disponíveis.</div>
      </section>
    );
  }

  return (
    <section style={{
      backgroundColor: SYSTEM_COLORS.WHITE.WHITE,
      border: `1px solid ${SYSTEM_COLORS.GRAY.LIGHT_GRAY}`,
      borderRadius: "12px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      padding: "20px 24px",
      maxBlockSize: "62vh",
      display: "flex",
      flexDirection: "column",
    }}>

      <hgroup style={{ marginBlockEnd: '0.4rem' }}>
        <h6 style={{ margin: 0 }}>
          Perda Potencial de Venda (R$)
        </h6>

        <p style={{ fontSize: "0.6rem", color: SYSTEM_COLORS.GRAY.GREY }}>
          Valor estimado de perda de venda por falta de estoque
        </p>
      </hgroup>

      <div style={{ flex: 1, overflow: "auto", marginTop: "12px" }}>
        <table style={{ inlineSize: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{
                textAlign: "left",
                padding: "0.4rem",
                color: SYSTEM_COLORS.BLACK.EERIE_BLACK,
                fontWeight: "700",
                fontSize: "0.7rem",
                borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`,
                borderRight: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`,
                backgroundColor: SYSTEM_COLORS.WHITE.WHITE,
                position: "sticky",
                top: 0,
                zIndex: 2,
              }}>
                {textFormatter(t("category"), { textTransform: 'uppercase' })}
              </th>

              {otbMonths.map(({ month, year, label }) => (
                <th key={`${month}-${year}`} style={{
                  color: SYSTEM_COLORS.BLACK.EERIE_BLACK,
                  fontWeight: "700",
                  textAlign: "center",
                  fontSize: "0.7rem",
                  borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`,
                  borderRight: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`,
                  backgroundColor: "#fdf3f3",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                }}>
                  {label}
                </th>
              ))}

              <th style={{
                padding: "8px 10px",
                color: SYSTEM_COLORS.BLACK.EERIE_BLACK,
                fontWeight: "700",
                fontSize: "0.7rem",
                borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`,
                backgroundColor: "#fdf3f3",
                position: "sticky",
                top: 0,
                zIndex: 1,
              }}>
                {textFormatter(t("total"), { textTransform: 'uppercase' })}
              </th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row) => (
              <tr
                key={row.categoria}
                className="otb-row"
                style={{ borderBottom: "1px solid #f5f5f5" }}>

                <td
                  className="otb-cell"
                  style={{
                    "--bg": SYSTEM_COLORS.WHITE.WHITE,
                    "--bg-hover": "rgba(0,0,0,0.03)",
                    backgroundColor: "var(--bg)",
                    padding: "0.4rem",
                    color: "#333",
                    fontWeight: "400",
                    borderRight: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`,
                    fontSize: "0.8rem",
                  }}
                >
                  {row.categoria}
                </td>

                {row.valores.map((value, colIndex) => (
                  <td
                    key={colIndex}
                    className="otb-cell"
                    style={{
                      "--bg": cellBgPerda(value),
                      "--bg-hover": cellBgPerda(value, true),
                      backgroundColor: "var(--bg)",
                      textAlign: "center",
                      padding: "0.4rem",
                      color: value == null ? SYSTEM_COLORS.GRAY.GRAY_187 : "#d97706",
                      fontWeight: value == null ? "400" : "500",
                      borderRight: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`,
                      fontSize: "0.8rem"
                    }}
                  >
                    {value == null ? "—" : currencyFormatter.format(value)}
                  </td>
                ))}

                <td
                  className="otb-cell"
                  style={{
                    "--bg": SYSTEM_COLORS.WHITE.WHITE,
                    "--bg-hover": "rgba(0,0,0,0.05)",
                    backgroundColor: "var(--bg)",
                    textAlign: "center",
                    color: row.total == null ? SYSTEM_COLORS.GRAY.GRAY_187 : "#d97706",
                    fontWeight: row.total == null ? "400" : "700",
                    fontSize: "0.8rem"
                  }}
                >
                  {row.total == null ? "—" : currencyFormatter.format(row.total)}
                </td>
              </tr>
            ))}
          </tbody>

          <tfoot>
            <tr style={{
              borderTop: "2px solid #e8e8e8",
              backgroundColor: "#f5f5f5"
            }}>
              <td style={{
                padding: "0.4rem",
                fontSize: "0.7rem",
                fontWeight: "700",
                color: SYSTEM_COLORS.BLACK.EERIE_BLACK,
                borderRight: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`
              }}>
                {textFormatter(t("total"), { textTransform: 'uppercase' })}
              </td>

              {monthTotals.map((value, index) => (
                <td
                  key={index}
                  style={{
                    padding: "0.4rem",
                    textAlign: "center",
                    borderRight: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`,
                    fontSize: "0.75rem",
                    fontWeight: "700",
                    color: "#d97706",
                  }}>
                  {currencyFormatter.format(value)}
                </td>
              ))}

              <td style={{
                padding: "0.4rem",
                textAlign: "center",
                backgroundColor: "#f5f5f5",
                fontSize: "0.75rem",
                fontWeight: "700",
                color: "#d97706",
              }}>
                {currencyFormatter.format(grandTotal)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
};

export function OpenVsExcessosSection({ actions, ctx }) {

  const { openNovoOtbModal, setSelectedCycleId } = actions || {};

  const { 
    openToBuyByCategoryTableData, 
    openToBuyCategorySalesLossRisk,
    otbCycles,
    selectedCycleId
  } = ctx || {};

  const cycleOptions = useMemo(() => {
    return (otbCycles || []).map(otb => ({
      label: otb.otbName,
      value: otb.cycleId,
    }));
  }, [otbCycles]);

  const parentLevelOptions = useMemo(() => {
    const keys = Object.keys(openToBuyByCategoryTableData || {});

    return keys.map((key, index) => ({
      label: key,
      value: index,
    }));
  }, [openToBuyByCategoryTableData]);

  const [selectedParentLevel, setSelectedParentLevel] = useState();

  const filteredCategoryData = useMemo(() => {
    return mergeGroups(openToBuyByCategoryTableData);
  }, [openToBuyByCategoryTableData]);

  const filteredSalesLossData = useMemo(() => {
    return mergeGroups(openToBuyCategorySalesLossRisk);
  }, [openToBuyCategorySalesLossRisk]);

  const { openToBuyTotalValue, grossExcessTotalValue, salesLossRiskTotalValue } = useMemo(() => {
    let openToBuyTotalValue = 0;
    let grossExcessTotalValue = 0;
    let salesLossRiskTotalValue = 0;

    Object.values(filteredCategoryData).forEach(rows => {
      rows.forEach(row => {
        openToBuyTotalValue += Math.abs(Number(row.excess ?? 0));
        grossExcessTotalValue += Number(row.open ?? 0);
      });
    });

    Object.values(filteredSalesLossData).forEach(rows => {
      rows.forEach(row => {
        salesLossRiskTotalValue += Number(row.salesLoss ?? 0);
      });
    });

    return {
      openToBuyTotalValue,
      grossExcessTotalValue,
      salesLossRiskTotalValue,
    };
  }, [filteredCategoryData, filteredSalesLossData]);

  const kpis = buildKpis({ openToBuyTotalValue, grossExcessTotalValue, salesLossRiskTotalValue });

  const chartData = useMemo(() => {
    const map = {};

    Object.values(filteredCategoryData).flat().forEach(row => {
      const key = `${MONTH_NAMES[row.month - 1]}/${String(row.year).slice(-2)}`;
      if (!map[key]) map[key] = { mes: key, abertura: 0, excesso: 0, month: row.month, year: row.year };
      map[key].abertura += Math.abs(Number(row.excess ?? 0));
      map[key].excesso += Number(row.open ?? 0);
    });

    return Object.values(map).sort((a, b) => {
      if (a.year === b.year) {
        return a.month - b.month;
      }
      return a.year - b.year;
    });
  }, [filteredCategoryData]);


  useEffect(() => {
    if (selectedParentLevel == null && parentLevelOptions.length) {
      setSelectedParentLevel(parentLevelOptions[0].value);
    }
  }, [parentLevelOptions, selectedParentLevel]);

  useEffect(() => {
    const currentLabel = parentLevelOptions.find(o => o.value === selectedParentLevel)?.label;
    const stillValid = currentLabel != null && parentLevelOptions.some(o => o.label === currentLabel);
    if (!stillValid && parentLevelOptions.length) setSelectedParentLevel(parentLevelOptions[0].value);
  }, [parentLevelOptions, selectedParentLevel]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Cabeçalho */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <hgroup>
          <h1 style={{ fontSize: "22px", fontWeight: "700", color: SYSTEM_COLORS.BLACK.EERIE_BLACK, margin: 0 }}>
            Abertura vs Excessos
          </h1>
          <p style={{ fontSize: "13px", color: SYSTEM_COLORS.GRAY.GREY, margin: "3px 0 0" }}>
            Abertura de compra e excessos por categoria e mês projetado
          </p>
        </hgroup>

        <div style={{ display: "flex", alignItems: "stretch", gap: 10 }}>
          <StableSelect
            label=""
            mode="rovitex"
            value={selectedCycleId}
            onChange={setSelectedCycleId}
            options={cycleOptions}
            showCheckmark={true}/>

          {/* <StableSelect
            label=""
            mode="rovitex"
            value={selectedParentLevel}
            onChange={setSelectedParentLevel}
            options={parentLevelOptions}
            showCheckmark={true}/> */}

          {/* <button
            onClick={openNovoOtbModal}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "0 20px", border: "1px solid #ddd",
              borderRadius: "8px", background: SYSTEM_COLORS.WHITE.WHITE, cursor: "pointer",
              fontSize: "14px", color: SYSTEM_COLORS.BLACK.EERIE_BLACK, fontWeight: "600", whiteSpace: "nowrap",
            }}
          >
            + Novo OTB
          </button> */}
        </div>
      </div>
      
      {/* KPIs cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
        {kpis.map((kpi) => (
          <div key={kpi.label} style={{
            backgroundColor: SYSTEM_COLORS.WHITE.WHITE,
            border: `1px solid ${SYSTEM_COLORS.GRAY.LIGHT_GRAY}`,
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            padding: "20px 24px",
          }}>
            <div style={{ fontSize: "11px", fontWeight: "600", color: SYSTEM_COLORS.GRAY.GREY, letterSpacing: "0.5px", marginBottom: 8 }}>
              {kpi.label}
            </div>
            <div style={{ fontSize: "32px", fontWeight: "700", color: kpi.textColor, lineHeight: 1.1 }}>
              {kpi.value}
            </div>
            <div style={{ fontSize: "12px", color: SYSTEM_COLORS.GRAY.GREY, marginTop: 4 }}>
              {kpi.subtitle}
            </div>
          </div>
        ))}
      </div>
      
      {/* Chart */}
      <section style={{ 
        backgroundColor: SYSTEM_COLORS.WHITE.WHITE,
        border: `1px solid ${SYSTEM_COLORS.GRAY.LIGHT_GRAY}`,
        borderRadius: "12px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)", padding: "20px 24px" }}>

        <hgroup style={{ marginBlockEnd: "1rem" }}>
          <h6 style={{ fontWeight: "700", color: SYSTEM_COLORS.BLACK.EERIE_BLACK }}>
            Excesso vs Abertura por Mês (peças)
          </h6>
        </hgroup>

        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="#f0f0f0" />
            <XAxis dataKey="mes" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: SYSTEM_COLORS.GRAY.TAUPE_GRAY }} />
            <YAxis
              tickFormatter={(v) => {
                const abs = Math.abs(v);
                return abs >= 1000 ? (abs / 1000).toFixed(0) + "k" : abs;
              }}
              axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: SYSTEM_COLORS.GRAY.TAUPE_GRAY }}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(0,0,0,0.05)" }} />
            <Bar dataKey="abertura" name="abertura" fill={SYSTEM_COLORS.RED.BRICK_RED} radius={[3, 3, 3, 3]} maxBarSize={40} />
            <Bar dataKey="excesso" name="excesso" fill={SYSTEM_COLORS.GREEN.FOREST_GREEN} radius={[3, 3, 3, 3]} maxBarSize={40} />
          </BarChart>
        </ResponsiveContainer>
      </section>

      <OpenToBuyByCategoryTable ctx={{ otbDataByCategory: filteredCategoryData }}/>

      <PerdaPotencialTable ctx={{ potencialSalesLossByCategory: filteredSalesLossData }} />
    </div>
  );
};